import { style, globalStyle } from '@vanilla-extract/css';
import { vars } from '../../theme/contract.css';
import { mobile } from '../../theme/media';
import {
  glassRimLight,
  glassRefractionBeforeBase,
  glassGlowLine,
  glassGlowBleed,
  textOnGlassStrong,
} from '../../styles/glass.css';

export const container = style({
  ...textOnGlassStrong,
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  // 휴가자 상세 패널이 열려 그리드+상세+범례가 카드 높이를 넘으면 카드를 스크롤.
  // (높이 부족은 그리드가 먼저 shrink 로 흡수하되 grid.minHeight 하한까지만 —
  //  그 아래로는 안 줄어들므로 여기가 최후 수단이 된다)
  overflowY: 'auto',
  position: 'relative',
  contain: 'layout paint',
  isolation: 'isolate',
  backgroundColor: vars.surface.liquidGlass.innerSurface.background,
  backgroundImage: `radial-gradient(ellipse at 50% 40%, ${vars.opacityScale.whiteOverlay.light} 0%, transparent 40%, ${vars.opacityScale.blackOverlay.fade} 100%)`,
  border: `1px solid ${vars.surface.liquidGlass.innerSurface.border}`,
  borderRadius: vars.spacing.borderRadius.lg,
  padding: 20,
  boxShadow: `inset 0 0 20px ${vars.opacityScale.blackOverlay.medium}, inset 0 1px 0 ${vars.opacityScale.whiteOverlay.veil}, inset 0 -2px 6px ${vars.opacityScale.blackOverlay.strong}, 0 0 6px ${vars.bright.primary.glow}`,
  containerType: 'inline-size',
  backdropFilter: vars.surface.liquidGlass.backdropFilter.light,
  '@media': {
    [mobile]: {
      padding: 12,
      borderRadius: 8,
    },
  },
});

globalStyle(`${container}::before`, glassRefractionBeforeBase);
globalStyle(`${container}::after`, glassRimLight);

export const glowEffect = style({
  ...glassGlowLine,
  zIndex: vars.elevation.decorative,
});

export const glowBleedEffect = style({
  ...glassGlowBleed,
  zIndex: vars.elevation.base,
});

/**
 * 제목 줄. 제목(또는 월 선택)이 왼쪽에 남는다 — 이 줄에 `auto` 마진이나 `space-between` 을
 * 쓰지 말 것. 2026-08-25 에 이동 버튼을 제목 앞에 두고 `marginInlineStart: auto` 로 밀었다가
 * 여백이 버튼 앞에서 벌어져 제목까지 오른쪽으로 끌려간 회귀가 있었다.
 */
export const titleRow = style({
  display: 'flex',
  alignItems: 'center',
});

/**
 * 월 선택이 앉은 줄. 제목은 접근성 트리로만 남고 select 가 자리를 차지하므로, `title` 이
 * 들고 있던 상·하 마진을 이 줄이 대신 든다 — 안 그러면 줄 높이가 달라져 그리드 배분이 흔들린다.
 */
export const titleRowSelectable = style({
  marginTop: '0.6em',
  marginBottom: 10,
  '@media': {
    [mobile]: {
      marginBottom: 8,
    },
  },
});

/** 폭은 내용에 맞춘다 — 카드 전체로 늘어나면 달 하나 고르는 컨트롤이 헤더를 다 먹는다. */
export const monthSelect = style({
  fontFamily: vars.typography.fontFamily.mono,
  fontWeight: vars.typography.fontWeight.bold,
});

export const title = style({
  fontFamily: vars.typography.fontFamily.mono,
  fontWeight: vars.typography.fontWeight.bold,
  fontSize: vars.typography.fontSize.base,
  color: vars.typography.textColor.primary,
  // h3→h2 승격 시 기존 h3의 UA 상단 마진(1em)을 그대로 보존해 픽셀 레이아웃 유지
  marginTop: '1em',
  marginBottom: 16,
  '@media': {
    [mobile]: {
      fontSize: vars.typography.fontSize.sm,
      marginBottom: 12,
    },
  },
});

export const weekdayHeader = style({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: 1,
  marginBottom: 2,
  backgroundColor: vars.surface.liquidGlass.innerSurface.background,
  backdropFilter: vars.surface.liquidGlass.backdropFilter.strong,
  WebkitBackdropFilter: vars.surface.liquidGlass.backdropFilter.strong,
  borderRadius: 4,
  border: `1px solid ${vars.surface.liquidGlass.innerSurface.border}`,
  overflow: 'hidden',
});

export const weekdayLabel = style({
  textTransform: 'uppercase',
  fontFamily: vars.typography.fontFamily.mono,
  fontSize: '3cqw',
  color: vars.typography.textColor.tertiary,
  letterSpacing: vars.typography.letterSpacing.expanded,
  textAlign: 'center',
  height: '4cqw',
  minHeight: 14,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRight: `1px solid ${vars.surface.liquidGlass.innerSurface.border}`,
  selectors: {
    '&:last-child': { borderRight: 'none' },
  },
});

export const grid = style({
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  width: '100%',
  maxWidth: '65vh',
  margin: '0 auto',
  // Why: 창을 세로로 줄이면 그리드도 함께 줄어야 한다(shrink 허용).
  // shrink:0 이면 flexBasis:auto 와 맞물려 그리드가 콘텐츠 max-content 높이에 고정돼
  // 창에 세로 스크롤바가 생긴다.
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: 'auto',
  // Why: 282 = 6행 × 41.6px(읽기 가능 하한) + 그리드 고정 항 32.5.
  // 셀 폰트는 cqw(컨테이너 width) 기반이라 세로가 줄어도 안 줄어든다 → 행이 이보다 낮아지면
  // 내용이 잘린다. 여기서 shrink 를 멈추고 그 아래는 container 가 스크롤한다.
  //
  // 행 하한은 숫자 한 줄이 아니라 숫자 + 그 아래 라벨 한 줄을 센다. chromium 실측(폰트 상한이
  // 물리는 grid 596px, 2026-08-27): 라벨 있는 셀의 콘텐츠 자연 높이 39.15px
  //   = 숫자 24.5(min(5cqw,20px) 라인박스) + cellWithNames gap 2 + 라벨 12.65(11px × 1.15)
  // 41.6 은 거기에 행당 2.4px 여유를 얹은 값이다. 딱 맞춰 잡으면(하한 270, 여유 0.38px) 폰트
  // 폴백으로 라인박스가 1px 만 커져도 다시 잘린다 — 여유가 이 하한의 존재 이유다.
  // 고정 항 27.4 = weekdayHeader(4cqw 라 넓은 창에서 여기까지 큰다: 라벨 23.4 + 테두리 2 +
  // marginBottom 2). 좁은 창에서는 18 로 줄지만 하한은 넓은 쪽으로 잡는다 — 잘림이 발생하는
  // 쪽이 넓은 창이기 때문이다. (이 줄은 한때 '행 gap 5' 를 함께 셌지만 `grid` 의 gap 은 처음부터
  // 0 이다. 282 라는 값 자체는 그 5px 가 행으로 돌아가 행 41.6 → 실측 41.59 로 성립한다.)
  //
  // 이전 값 220 은 숫자만 놓고 뽑은 행 32px 이라, 공휴일명·휴가자 이름이 붙는 셀에서 라벨이
  // 셀의 overflow:hidden 에 잘렸다. 창을 세로로만 줄이면 폭은 그대로여서 폰트가 상한에 붙은
  // 채 행만 눌리는 비대칭이 원인이다(CalendarHeatmapCell.cell 주석의 같은 함정).
  //
  // ponytail: 라벨 한 줄까지만 센다. 휴가자 2줄 셀(이름 블록 26.3px, 행 52.4px 필요)은 이
  // 하한에서 여전히 잘린다 — 거기까지 세면 하한이 347 로 max-content(363)에 붙어 PR#1378 이
  // 없앤 상시 스크롤바가 돌아온다. 2줄까지 덮으려면 하한 상향이 아니라 짧은 행에서 이름을
  // 1줄 + "+N" 으로 접는 쪽이어야 한다(셀 쪽 변경).
  minHeight: 282,
  containerType: 'inline-size',
});

export const gridBody = style({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  // Why: 행 하한은 여기가 아니라 grid.minHeight(282) 가 책임진다. 두 곳에 하한을 두면
  // 서로 어긋나므로 트랙은 가용 높이를 행 수로 그대로 분배만 한다.
  // (스크롤바의 지배적 원인은 이 42px 하한이 아니라 grid 의 flexShrink:0 + flexBasis:auto 가
  //  그리드를 max-content 높이(행 56px)에 고정한 것이었다 — 하한 부활이 원인이 아니다)
  gridAutoRows: 'minmax(0, 1fr)',
  flex: 1,
  minHeight: 0,
  gap: 1,
  backgroundColor: vars.surface.liquidGlass.innerSurface.border,
  borderRadius: 4,
  overflow: 'hidden',
});

export const emptyCell = style({
  backgroundColor: vars.surface.liquidGlass.innerSurface.backgroundL1,
});

export const legend = style({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 16,
  margin: 0,
  marginTop: 12,
  paddingTop: 12,
  borderTop: `1px solid ${vars.surface.liquidGlass.innerSurface.border}`,
  '@media': {
    [mobile]: {
      gap: 10,
      marginTop: 8,
      paddingTop: 8,
    },
  },
});

export const legendSection = style({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
});

export const legendTerm = style({
  display: 'inline-flex',
  alignItems: 'center',
  margin: 0,
  padding: 0,
});

export const legendLabel = style({
  margin: 0,
  fontFamily: vars.typography.fontFamily.mono,
  fontSize: vars.typography.fontSize['2xs'],
  color: vars.typography.textColor.tertiary,
  '@media': {
    [mobile]: {
      fontSize: vars.typography.fontSize['3xs'],
    },
  },
});

export const legendGradient = style({
  width: 48,
  height: 10,
  display: 'inline-block',
  borderRadius: 2,
  background: `linear-gradient(90deg, transparent 0%, ${vars.bright.tailwind.green500Bold} 100%)`,
  border: `1px solid ${vars.surface.liquidGlass.innerSurface.border}`,
  '@media': {
    [mobile]: {
      width: 36,
      height: 8,
    },
  },
});

export const legendStripe = style({
  width: 16,
  height: 3,
  display: 'inline-block',
  borderRadius: 1,
});

// 본인 휴가 줄 — 종류(연차/병가/특별)는 상세에서 읽는다. 색은 연차 스트라이프와 같다.
export const legendLeave = style({ backgroundColor: `${vars.bright.tailwind.indigoStrong}` });

// ─── 휴가자 상세 패널 ───

export const othersDetail = style({
  marginTop: 12,
  padding: '10px 12px',
  borderRadius: 8,
  border: `1px solid ${vars.surface.liquidGlass.innerSurface.border}`,
  backgroundColor: vars.surface.liquidGlass.innerSurface.backgroundL1,
  '@media': {
    [mobile]: {
      marginTop: 8,
      padding: '8px 10px',
    },
  },
});

export const othersDetailHeader = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
});

export const othersDetailTitle = style({
  margin: 0,
  fontFamily: vars.typography.fontFamily.mono,
  fontSize: vars.typography.fontSize.sm,
  fontWeight: vars.typography.fontWeight.bold,
  color: vars.typography.textColor.primary,
});

export const othersDetailClose = style({
  appearance: 'none',
  background: 'none',
  border: 0,
  margin: 0,
  padding: 2,
  cursor: 'pointer',
  fontSize: vars.typography.fontSize.base,
  lineHeight: String(1),
  color: vars.typography.textColor.tertiary,
  ':focus-visible': {
    outline: `2px solid ${vars.bright.primary.default}`,
    outlineOffset: 1,
  },
});

export const othersGroup = style({
  marginTop: 8,
});

export const othersGroupTitle = style({
  margin: '0 0 4px',
  fontFamily: vars.typography.fontFamily.mono,
  fontSize: vars.typography.fontSize['2xs'],
  fontWeight: vars.typography.fontWeight.medium,
  color: vars.typography.textColor.tertiary,
  letterSpacing: vars.typography.letterSpacing.expanded,
});

export const othersList = style({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexWrap: 'wrap',
  gap: '4px 14px',
});

export const othersItem = style({
  fontFamily: vars.typography.fontFamily.mono,
  fontSize: vars.typography.fontSize.xs,
  color: vars.typography.textColor.secondary,
});
